/* eslint-disable @typescript-eslint/no-non-null-assertion */
import './fullcalendar-material.css';

import React, { forwardRef, useEffect, useState } from 'react';
import { IconButton, Text } from '@fluentui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { useQuery, useQueryClient } from 'react-query';
import {
  Event,
  schedulerEvents as events,
} from '@groove/api/services/v1/scheduler';
import {
  differenceInMinutes,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  areIntervalsOverlapping,
} from 'date-fns';
import moment from 'moment-timezone';
import { theme } from '@groove/ui/theme';

import useStore from '../store/useStore';
import {
  isDateStringDiff,
  getWeekStartDateAndIgnoreTimeZone,
} from '../utils/time';

const iconBtnStyles = {
  icon: 'text-[8px]',
};

type CalendarProps = {
  duration: number | null;
  timeZone: string | null;
  userIds: number[];
  userColors: { [key: string]: string };
  allowDoubleBooking: boolean;
  onIsEventsLoading: (isLoading: boolean) => void;
};
export type ParsedEvent = Event & {
  start: Date;
  end: Date;
  color: string;
  display?: string;
};

const isBusy = (event: Event | ParsedEvent): boolean => {
  return event.transparency !== 'transparent';
};

const Calendar = forwardRef<FullCalendar, CalendarProps>(
  (
    {
      duration,
      timeZone,
      userIds,
      userColors,
      allowDoubleBooking,
      onIsEventsLoading,
    },
    ref,
  ) => {
    const timeSlots = useStore(store => store.timeSlots);
    const insertTimeSlots = useStore(store => store.insertTimeSlots);
    const removeTimeSlot = useStore(store => store.removeTimeSlot);
    const clearTimeSlots = useStore(store => store.clearTimeSlots);
    const recalculateValidTimeSlots = useStore(
      store => store.recalculateValidTimeSlots,
    );
    const queryClient = useQueryClient();

    const [currentTime, setCurrentTime] = useState(new Date());

    const {
      data: eventsData,
      isLoading,
      isStale,
    } = useQuery(
      ['events', currentTime.toString(), userIds],
      () =>
        events(getISOWeek(currentTime), getISOWeekYear(currentTime), userIds),
      {
        enabled: userIds.length > 0,
        onError: error => {
          console.log('error getting user events on calendar', error);
          queryClient.invalidateQueries('mail_and_calendar_provider');
        },
      },
    );

    /* For all-day events, the date should be set irrespective of time zones since parseISO was not giving the correct date.
    we have to manipulate the startDate and endDate based on the timezone getTimezoneOffset.
    Get the timezone offset for the selected timezone, UTCOffset() function returns the offset in minutes.
    compare the desired offset and the getTimezoneOffset() of the startDate.
    if the current offset is behind the desired offset, so we add 24 hours in milliseconds.
    then calculate the start date in UTC by adding up the timestamp of startDate in milliseconds
    and he current offset in milliseconds and desired offset
     */
    const userEvents = eventsData?.data?.map(event => {
      const offset = moment.tz(timeZone || 'UTC').utcOffset() * -1;

      const startDate = new Date(event.start);
      const startDateOffset =
        offset > startDate.getTimezoneOffset() ||
        (timeZone === 'America/Anchorage' &&
          offset === startDate.getTimezoneOffset())
          ? 24 * 60 * 60 * 1000
          : 0;
      const startDateUTC = new Date(
        startDate.getTime() +
          startDate.getTimezoneOffset() * 60000 +
          startDateOffset,
      );

      const endDate = new Date(event.end);
      const endDateOffset =
        offset > endDate.getTimezoneOffset() ||
        (timeZone === 'America/Anchorage' &&
          offset === endDate.getTimezoneOffset())
          ? 24 * 60 * 60 * 1000
          : 0;
      const endDateUTC = new Date(
        endDate.getTime() + endDate.getTimezoneOffset() * 60000 + endDateOffset,
      );

      return {
        ...event,
        title: event.title || (isBusy(event) ? 'Busy' : 'Free'),
        start: event.is_all_day ? startDateUTC : parseISO(event.start),
        end: event.is_all_day ? endDateUTC : parseISO(event.end),
        color: !isBusy(event) ? theme.gray4 : userColors[event.user_id],
        display: !event.is_all_day ? 'background' : '',
      } as ParsedEvent;
    });

    let combinedEvents = Array.from(timeSlots.values());

    if (userEvents) {
      combinedEvents = combinedEvents.concat(userEvents);
    }

    useEffect(() => {
      if (eventsData) {
        recalculateValidTimeSlots(eventsData.data);
      }
    }, [recalculateValidTimeSlots, eventsData]);

    useEffect(() => {
      if (duration) {
        clearTimeSlots();
      }
    }, [clearTimeSlots, duration]);

    useEffect(() => {
      onIsEventsLoading(isLoading && isStale);
    }, [isLoading, isStale, onIsEventsLoading]);

    return (
      <div className="flex-grow max-h-screen">
        <FullCalendar
          height="100%"
          ref={ref}
          plugins={[timeGridPlugin, interactionPlugin, momentTimezonePlugin]}
          initialView="timeGridWeek"
          allDaySlot={eventsData?.data.some(
            e => e.is_all_day || e.is_greater_than_24_hours,
          )}
          nowIndicator
          eventOrder="-title"
          firstDay={1}
          datesSet={({ start, startStr }) => {
            if (isDateStringDiff(start, currentTime)) {
              setCurrentTime(getWeekStartDateAndIgnoreTimeZone(startStr));
            }
          }}
          timeZone={timeZone || 'local'}
          eventColor={theme['primary-dark-alt']}
          eventClick={eventObj => {
            const target = (eventObj.jsEvent.target as Element) || null;
            if (
              target?.classList.contains('ms-Button') ||
              target?.classList.contains('ms-Button-icon')
            ) {
              removeTimeSlot(eventObj.event.start!);
            }
          }}
          selectable
          scrollTime="08:00:00"
          snapDuration={{ minutes: duration || 30 }}
          events={combinedEvents}
          eventContent={content => {
            const duration = differenceInMinutes(
              content.event.end!,
              content.event.start!,
            );
            return (
              <div
                className="flex overflow-hidden"
                style={{ height: 'inherit' }}
              >
                {duration <= 45 && (
                  <Text
                    variant="medium"
                    block
                    className={
                      content.event.extendedProps.isTimeSlot
                        ? 'text-white'
                        : 'text-black text-[10px] font-semibold'
                    }
                  >
                    {content.event.title || content.timeText}
                  </Text>
                )}
                {duration > 45 && (
                  <div>
                    <Text
                      variant="medium"
                      block
                      className={
                        content.event.extendedProps.isTimeSlot
                          ? 'text-white'
                          : 'text-black text-[10px] font-semibold'
                      }
                    >
                      {content.timeText}
                    </Text>
                    <Text
                      variant="medium"
                      block
                      className={
                        content.event.extendedProps.isTimeSlot
                          ? 'text-white'
                          : 'text-black text-[10px] font-semibold'
                      }
                    >
                      {content.event.title}
                    </Text>
                  </div>
                )}
                {content.event.extendedProps.isTimeSlot && (
                  <IconButton
                    ariaLabel="Remove Time Slot"
                    className="w-4 h-4 ml-auto text-white"
                    styles={iconBtnStyles}
                    iconProps={{ iconName: 'ChromeClose' }}
                  />
                )}
              </div>
            );
          }}
          eventDataTransform={event => {
            if (!event) return {};
            return {
              ...event,
              allDay: event.is_all_day || event.is_greater_than_24_hours,
              color:
                event.extendedProps?.isTimeSlot &&
                !allowDoubleBooking &&
                userEvents?.some(
                  userEvent =>
                    areIntervalsOverlapping(userEvent, event as Interval) &&
                    isBusy(userEvent),
                )
                  ? 'red'
                  : event.color,
            };
          }}
          eventDidMount={arg => {
            arg.el.title = arg.event.title;
          }}
          selectMirror
          select={arg => {
            // After selecting, the placeholder sticks around, so
            // we need to programmatically remove it.
            arg.view.calendar.unselect();
            insertTimeSlots(
              arg.start,
              arg.end,
              duration || 30,
              userEvents || [],
            );
          }}
          selectAllow={date => date.start > new Date()}
        />
      </div>
    );
  },
);

export default Calendar;
